import React, { useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Textarea from '../shared/TailwindComponents/Textarea';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import {
  addGlobalRule,
  updateGlobalRule,
} from '../../store/actions/Review/ReviewActions';
import { GlobalRule, FindingSeverity } from '../../types/review';

interface GlobalRuleFormPanelProps {
  reviewUuid: string;
  rule?: GlobalRule;
  onClose: () => void;
}

const severityConfig: { key: FindingSeverity; label: string; color: string; activeColor: string }[] = [
  { key: 'critical', label: 'Critical', color: 'border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20', activeColor: 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-1 ring-red-500' },
  { key: 'major', label: 'Major', color: 'border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20', activeColor: 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 ring-1 ring-orange-500' },
  { key: 'minor', label: 'Minor', color: 'border-yellow-300 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20', activeColor: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 ring-1 ring-yellow-500' },
  { key: 'info', label: 'Info', color: 'border-blue-300 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20', activeColor: 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500' },
];

const GlobalRuleFormPanel = forwardRef<any, GlobalRuleFormPanelProps>(({ reviewUuid, rule, onClose }, ref) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isEditing = !!rule;

  const [ruleText, setRuleText] = useState(rule?.rule_text || '');
  const [severity, setSeverity] = useState<FindingSeverity>(rule?.severity || 'major');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!ruleText.trim()) return;
    setIsSaving(true);
    try {
      if (isEditing && rule) {
        await dispatch(updateGlobalRule(reviewUuid, rule.uuid, { rule_text: ruleText, severity }) as any);
      } else {
        await dispatch(addGlobalRule(reviewUuid, { rule_text: ruleText, severity }) as any);
      }
      onClose();
    } catch (err: any) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: err?.message || t('Failed to save global rule'),
        duration: 3000,
      }) as any);
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, reviewUuid, ruleText, severity, isEditing, rule, onClose, t]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  return (
    <div className="p-6 space-y-6">
      <Textarea
        id="panel-rule-text"
        label={t('Rule Text')}
        placeholder={t('e.g. No liability caps below $1,000,000')}
        value={ruleText}
        onChange={(e) => setRuleText(e.target.value)}
        rows={4}
        required
      />

      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">
          {t('Severity')}
        </label>
        <div className="grid grid-cols-4 gap-2">
          {severityConfig.map((sev) => (
            <button
              key={sev.key}
              type="button"
              onClick={() => setSeverity(sev.key)}
              className={clsx(
                'px-3 py-2 rounded-lg text-xs font-medium border transition-all text-center',
                severity === sev.key ? sev.activeColor : sev.color,
              )}
            >
              {t(sev.label)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

export default GlobalRuleFormPanel;
